import React, { useRef } from 'react';
import { AnyIfEmpty } from 'react-redux';
import { PropertyModalProps } from '../PropertyInformationModalBody';
import DownArrowIcon from '../../../svgIcons/DownArrowIcon';
import { geocodeByPlaceId } from 'react-google-places-autocomplete';
import parsePhoneNumberFromString from 'libphonenumber-js';
import { IRootState } from '../../../../redux/rootReducer';
import { useSelector } from 'react-redux';
import styles from './LogsAccordion.module.css';
import moment from 'moment';
import { getLocationTimeZone } from '../../../../constants/constants';
interface LogsAccordionProps {
    data: PropertyModalProps;
    active: boolean;
    onToggle: (index: AnyIfEmpty<object>) => void;
}

export interface Locker {
    id: string;
    lockerName: string;
}

const LogsAccordion = ({ data, active, onToggle }: LogsAccordionProps) => {
    const contentEl: AnyIfEmpty<object> = useRef();
    const now = moment().startOf('day');
    const date = moment.utc(data.updatedAt).startOf('day');
    const daysAgo = moment(moment(now).format('MM-DD-YYYY')).diff(moment(date).format('MM-DD-YYYY'), 'days');
    const { data: lockerData } = useSelector((state: IRootState) => state.lockerTypeReducer);

    const getLockerVersion = (lockerId: string | null | undefined): string | null => {
        if (!lockerId) return null;
        return lockerData?.find((locker: Locker) => locker?.id === lockerId)?.lockerName || null;
    };

    const isDifferentImages = (oldImages: string[], newImages: string[]) => {
        let isDiffernt = false;

        if (oldImages.length !== newImages.length) {
            isDiffernt = true;
        } else if (oldImages.length === newImages.length) {
            oldImages.forEach((value: string, index: number) => {
                if (value !== newImages[index]) {
                    isDiffernt = true;
                }
            });
        }
        return isDiffernt;
    };

    const isEmptyOrNull = (value: string | null) => {
        if (value?.trim() === '' || value === null) {
            return true;
        } else {
            return false;
        }
    };

    const fetchPlaceDetails = async (placeId: string) => {
        try {
            const results = await geocodeByPlaceId(placeId);
            const place = results[0];

            if (place) {
                return place.formatted_address as string;
            }
        } catch (error) {
            console.error('Error fetching place details:', error);
            return '';
        }
    };

    const FetchPlace: React.FC<{ placeId: string }> = ({ placeId }) => {
        const [placeName, setPlaceName] = React.useState<string>('Loading...');
        fetchPlaceDetails(placeId).then((response: string | undefined) => setPlaceName(response || ''));
        return <>{placeName}</>;
    };

    const hasChanged = (oldValue: string | null | undefined, newValue: string | null | undefined): boolean => {
        const normalize = (val: typeof oldValue) => {
            if (typeof val === 'string' && val.trim() === '') return null;
            return val === undefined ? null : val;
        };

        return normalize(oldValue) !== normalize(newValue);
    };

    function formatPhone(phone?: string): string {
        if (!phone) return '';
        const parsed = parsePhoneNumberFromString(phone);
        return parsed ? parsed.formatInternational() : phone;
    }

    return (
        <div className={styles.questionnarieContent}>
            <button className={`${styles.accordion} ${active ? styles.active : ''}`} onClick={onToggle}>
                <div>
                    <p className={styles.accordion__title}>{data.adminName}</p>
                    <p className={styles.accordion__email}>{data.adminEmail}</p>
                </div>
                <div className={styles.rightContainer}>
                    <div className={styles.timeWrapper}>
                        <p>{moment(data.updatedAt).locale('en').format('MMM DD, YYYY hh:mm A')}</p>
                        <p>{daysAgo === 0 ? 'Today' : daysAgo === 1 ? 'Yesterday' : `${daysAgo} days ago`}</p>
                    </div>
                    <div className={styles.arrowIconWrapper}>
                        <div className={`${styles.arrowIcon} ${active ? styles.active : ''}`}>
                            <DownArrowIcon />
                        </div>
                    </div>
                </div>
            </button>
            <div
                ref={contentEl}
                style={{ maxHeight: `${active ? `${contentEl.current?.scrollHeight}px` : '0px'}` }}
                className={styles.accordionContent}>
                <div className={`${styles.row} ${styles.header}`}>
                    <div className={styles.col}>Old Value</div>
                    <div className={styles.col}>New Value</div>
                </div>
                <div>
                    {data.old_values.name != data.new_values.name && (
                        <div className={styles.row}>
                            <div className={styles.col}>
                                <p>
                                    Name: <span>{data.old_values.name}</span>
                                </p>
                            </div>
                            <div className={styles.col}>
                                <p>
                                    Name: <span>{data.new_values.name}</span>
                                </p>
                            </div>
                        </div>
                    )}
                    {data.old_values.description !== data.new_values.description && (
                        <div className={styles.row}>
                            <div className={styles.col}>
                                <p>
                                    Description: <span>{data.old_values.description}</span>
                                </p>
                            </div>
                            <div className={styles.col}>
                                <p>
                                    Description: <span>{data.new_values.description}</span>
                                </p>
                            </div>
                        </div>
                    )}
                    {data.old_values.street1 !== data.new_values.street1 && (
                        <div className={styles.row}>
                            <div className={styles.col}>
                                <p>
                                    Street: <span>{data.old_values.street1}</span>
                                </p>
                            </div>
                            <div className={styles.col}>
                                <p>
                                    Street: <span>{data.new_values.street1}</span>
                                </p>
                            </div>
                        </div>
                    )}
                    {data.old_values.streetNumber !== data.new_values.streetNumber && (
                        <div className={styles.row}>
                            <div className={styles.col}>
                                <p>
                                    Street Number: <span>{data.old_values.streetNumber}</span>
                                </p>
                            </div>
                            <div className={styles.col}>
                                <p>
                                    Street Number: <span>{data.new_values.streetNumber}</span>
                                </p>
                            </div>
                        </div>
                    )}
                    {data.old_values.city !== data.new_values.city && (
                        <div className={styles.row}>
                            <div className={styles.col}>
                                <p>
                                    City: <span>{data.old_values.city}</span>
                                </p>
                            </div>
                            <div className={styles.col}>
                                <p>
                                    City: <span>{data.new_values.city}</span>
                                </p>
                            </div>
                        </div>
                    )}
                    {data.old_values.state !== data.new_values.state && (
                        <div className={styles.row}>
                            <div className={styles.col}>
                                <p>
                                    State: <span>{data.old_values.state}</span>
                                </p>
                            </div>
                            <div className={styles.col}>
                                <p>
                                    State: <span>{data.new_values.state}</span>
                                </p>
                            </div>
                        </div>
                    )}
                    {data.old_values.masterCode !== data.new_values.masterCode && (
                        <div className={styles.row}>
                            <div className={styles.col}>
                                <p>
                                    Master Code: <span>{data.old_values.masterCode}</span>
                                </p>
                            </div>
                            <div className={styles.col}>
                                <p>
                                    Master Code: <span>{data.new_values.masterCode}</span>
                                </p>
                            </div>
                        </div>
                    )}

                    {data.old_values.zip !== data.new_values.zip && (
                        <div className={styles.row}>
                            <div className={styles.col}>
                                <p>
                                    Zip Code: <span>{data.old_values.zip}</span>
                                </p>
                            </div>
                            <div className={styles.col}>
                                <p>
                                    Zip Code: <span>{data.new_values.zip}</span>
                                </p>
                            </div>
                        </div>
                    )}
                    {data.old_values.timeZone !== data.new_values.timeZone && (
                        <div className={styles.row}>
                            <div className={styles.col}>
                                <p>
                                    Timezone: <span>{getLocationTimeZone(data.old_values.timeZone)}</span>
                                </p>
                            </div>
                            <div className={styles.col}>
                                <p>
                                    Timezone: <span>{getLocationTimeZone(data.new_values.timeZone)}</span>
                                </p>
                            </div>
                        </div>
                    )}
                    {data.old_values.lockersCount !== data.new_values.lockersCount && (
                        <div className={styles.row}>
                            <div className={styles.col}>
                                <p>
                                    Lockers Count: <span>{data.old_values.lockersCount}</span>
                                </p>
                            </div>
                            <div className={styles.col}>
                                <p>
                                    Lockers Count: <span>{data.new_values.lockersCount}</span>
                                </p>
                            </div>
                        </div>
                    )}
                    {data.old_values.propertyStatus !== data.new_values.propertyStatus && (
                        <div className={styles.row}>
                            <div className={styles.col}>
                                <p>
                                    Property Status: <span>{data.old_values.propertyStatus}</span>
                                </p>
                            </div>
                            <div className={styles.col}>
                                <p>
                                    Property Status: <span>{data.new_values.propertyStatus}</span>
                                </p>
                            </div>
                        </div>
                    )}
                    {data.old_values.managementCompany !== data.new_values.managementCompany && (
                        <div className={styles.row}>
                            <div className={styles.col}>
                                <p>
                                    Management Company: <span>{data.old_values.managementCompany}</span>
                                </p>
                            </div>
                            <div className={styles.col}>
                                <p>
                                    Management Company: <span>{data.new_values.managementCompany}</span>
                                </p>
                            </div>
                        </div>
                    )}
                    {data.old_values.lockerName !== data.new_values.lockerName && (
                        <div className={styles.row}>
                            <div className={styles.col}>
                                <p>
                                    Locker Name: <span>{getLockerVersion(data.old_values.lockerName) || ''}</span>
                                </p>
                            </div>
                            <div className={styles.col}>
                                <p>
                                    Locker Name: <span>{getLockerVersion(data.new_values.lockerName) || ''}</span>
                                </p>
                            </div>
                        </div>
                    )}
                    {isDifferentImages(data.old_values.images, data.new_values.images) && (
                        <div className={styles.row}>
                            <div className={`${styles.col} ${styles.imageWrapper}`}>
                                <p>Property Images:</p>
                                {!!data.old_values.images.length && (
                                    <div className={styles.imageContainer}>
                                        {data.old_values.images.map((image: string, index: number) => {
                                            return (
                                                <div key={index} className={styles.imageBox}>
                                                    <img src={image} alt="property image " width={80} height={80} />
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                            <div className={`${styles.col} ${styles.imageWrapper}`}>
                                <p>Property Images:</p>
                                {!!data.new_values.images.length && (
                                    <div className={styles.imageContainer}>
                                        {data.new_values.images.map((image: string, index: number) => {
                                            return (
                                                <div key={index} className={styles.imageBox}>
                                                    <img src={image} alt="property image" width={80} height={80} />
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {data.old_values.reportSendTime !== data.new_values.reportSendTime && (
                        <div className={styles.row}>
                            <div className={styles.col}>
                                <p>
                                    Report Send Time: <span>{data.old_values.reportSendTime}</span>
                                </p>
                            </div>
                            <div className={styles.col}>
                                <p>
                                    Report Send Time: <span>{data.new_values.reportSendTime}</span>
                                </p>
                            </div>
                        </div>
                    )}

                    {data.old_values.appLink !== data.new_values.appLink && (
                        <div className={styles.row}>
                            <div className={styles.col}>
                                <p>
                                    Leasing Application Link:{' '}
                                    {data.old_values.appLink ? (
                                        <a href={data.old_values.appLink} target="_blank" rel="noreferrer noopner">
                                            {data.old_values.appLink}
                                        </a>
                                    ) : (
                                        ''
                                    )}
                                </p>
                            </div>
                            <div className={styles.col}>
                                <p>
                                    Leasing Application Link:{' '}
                                    {data.new_values.appLink ? (
                                        <a href={data.new_values.appLink} target="_blank" rel="noreferrer noopner">
                                            {data.new_values.appLink}
                                        </a>
                                    ) : (
                                        ''
                                    )}
                                </p>
                            </div>
                        </div>
                    )}

                    {data.old_values.contactLink !== data.new_values.contactLink && (
                        <div className={styles.row}>
                            <div className={styles.col}>
                                <p>
                                    Contact Link:{' '}
                                    {data.old_values.contactLink ? (
                                        <a href={data.old_values.contactLink} target="_blank" rel="noreferrer noopner">
                                            {data.old_values.contactLink}
                                        </a>
                                    ) : (
                                        ''
                                    )}
                                </p>
                            </div>
                            <div className={styles.col}>
                                <p>
                                    Contact Link:{' '}
                                    {data.new_values.contactLink ? (
                                        <a href={data.new_values.contactLink} target="_blank" rel="noreferrer noopner">
                                            {data.new_values.contactLink}
                                        </a>
                                    ) : (
                                        ''
                                    )}
                                </p>
                            </div>
                        </div>
                    )}

                    {((isEmptyOrNull(data.old_values.mappedInId) !== isEmptyOrNull(data.new_values.mappedInId) &&
                        data.old_values.mappedInId !== data.new_values.mappedInId) ||
                        (!isEmptyOrNull(data.old_values.mappedInId) &&
                            !isEmptyOrNull(data.new_values.mappedInId) &&
                            data.old_values.mappedInId !== data.new_values.mappedInId)) && (
                            <div className={styles.row}>
                                <div className={styles.col}>
                                    <p>
                                        Mapped In Id: <span>{data.old_values.mappedInId}</span>
                                    </p>
                                </div>
                                <div className={styles.col}>
                                    <p>
                                        Mapped In Id: <span>{data.new_values.mappedInId}</span>
                                    </p>
                                </div>
                            </div>
                        )}

                    {((isEmptyOrNull(data.old_values.mappedInFloorId) !== isEmptyOrNull(data.new_values.mappedInFloorId) &&
                        data.old_values.mappedInFloorId !== data.new_values.mappedInFloorId) ||
                        (!isEmptyOrNull(data.old_values.mappedInFloorId) &&
                            !isEmptyOrNull(data.new_values.mappedInFloorId) &&
                            data.old_values.mappedInFloorId !== data.new_values.mappedInFloorId)) && (
                            <div className={styles.row}>
                                <div className={styles.col}>
                                    <p>
                                        MappedIn Floor Id: <span>{data.old_values.mappedInFloorId}</span>
                                    </p>
                                </div>
                                <div className={styles.col}>
                                    <p>
                                        MappedIn Floor Id: <span>{data.new_values.mappedInFloorId}</span>
                                    </p>
                                </div>
                            </div>
                        )}

                    {data.old_values.unitMapId !== data.new_values.unitMapId && (
                        <div className={styles.row}>
                            <div className={styles.col}>
                                <p>
                                    Unit Map Id: <span>{data.old_values.unitMapId}</span>
                                </p>
                            </div>
                            <div className={styles.col}>
                                <p>
                                    Unit Map Id: <span>{data.new_values.unitMapId}</span>
                                </p>
                            </div>
                        </div>
                    )}

                    {data.old_values.unitMapAssetId !== data.new_values.unitMapAssetId && (
                        <div className={styles.row}>
                            <div className={styles.col}>
                                <p>
                                    Unit Map Asset Id: <span>{data.old_values.unitMapAssetId}</span>
                                </p>
                            </div>
                            <div className={styles.col}>
                                <p>
                                    Unit Map Asset Id: <span>{data.new_values.unitMapAssetId}</span>
                                </p>
                            </div>
                        </div>
                    )}

                    {((isEmptyOrNull(data.old_values.openViaChatBotPropertyId) !==
                        isEmptyOrNull(data.new_values.openViaChatBotPropertyId) &&
                        data.old_values.openViaChatBotPropertyId !== data.new_values.openViaChatBotPropertyId) ||
                        (!isEmptyOrNull(data.old_values.openViaChatBotPropertyId) &&
                            !isEmptyOrNull(data.new_values.openViaChatBotPropertyId) &&
                            data.old_values.openViaChatBotPropertyId !== data.new_values.openViaChatBotPropertyId)) && (
                            <div className={styles.row}>
                                <div className={styles.col}>
                                    <p>
                                        Open Via Chatbot id: <span>{data.old_values.openViaChatBotPropertyId}</span>
                                    </p>
                                </div>
                                <div className={styles.col}>
                                    <p>
                                        Open Via Chatbot id: <span>{data.new_values.openViaChatBotPropertyId}</span>
                                    </p>
                                </div>
                            </div>
                        )}

                    {!data.old_values.mapFilePath !== !data.new_values.mapFilePath && (
                        <div className={styles.row}>
                            <div className={styles.col}>
                                <p>
                                    Map File Path:{' '}
                                    {data.old_values.mapFilePath ? (
                                        <a href={data.old_values.mapFilePath} rel="noreferrer noopner" target="_blank">
                                            {data.old_values.mapFilePath}
                                        </a>
                                    ) : (
                                        ''
                                    )}
                                </p>
                            </div>
                            <div className={styles.col}>
                                <p>
                                    Map File Path:{' '}
                                    {data.new_values.mapFilePath ? (
                                        <a href={data.new_values.mapFilePath} rel="noreferrer noopner" target="_blank">
                                            {data.new_values.mapFilePath}
                                        </a>
                                    ) : (
                                        ''
                                    )}
                                </p>
                            </div>
                        </div>
                    )}

                    {Boolean(data.old_values.isPriceListed) !== Boolean(data.new_values.isPriceListed) && (
                        <div className={styles.row}>
                            <div className={styles.col}>
                                <p>
                                    Is Price Listed: <span>{data.old_values.isPriceListed ? 'True' : 'False'}</span>
                                </p>
                            </div>
                            <div className={styles.col}>
                                <p>
                                    Is Price Listed: <span>{data.new_values.isPriceListed ? 'True' : 'False'}</span>
                                </p>
                            </div>
                        </div>
                    )}

                    {Boolean(data.old_values.showCardDisclaimer) !== Boolean(data.new_values.showCardDisclaimer) && (
                        <div className={styles.row}>
                            <div className={styles.col}>
                                <p>
                                    Show Card Disclaimer: <span>{data.old_values.showCardDisclaimer ? 'True' : 'False'}</span>
                                </p>
                            </div>
                            <div className={styles.col}>
                                <p>
                                    Show Card Disclaimer: <span>{data.new_values.showCardDisclaimer ? 'True' : 'False'}</span>
                                </p>
                            </div>
                        </div>
                    )}

                    {Boolean(data.old_values.isCardRequired) !== Boolean(data.new_values.isCardRequired) && (
                        <div className={styles.row}>
                            <div className={styles.col}>
                                <p>
                                    Card Required: <span>{data.old_values.isCardRequired ? 'True' : 'False'}</span>
                                </p>
                            </div>
                            <div className={styles.col}>
                                <p>
                                    Card Required: <span>{data.new_values.isCardRequired ? 'True' : 'False'}</span>
                                </p>
                            </div>
                        </div>
                    )}

                    {Boolean(data.old_values.isDocumentRequired) !== Boolean(data.new_values.isDocumentRequired) && (
                        <div className={styles.row}>
                            <div className={styles.col}>
                                <p>
                                    Document Required: <span>{data.old_values.isDocumentRequired ? 'True' : 'False'}</span>
                                </p>
                            </div>
                            <div className={styles.col}>
                                <p>
                                    Document Required: <span>{data.new_values.isDocumentRequired ? 'True' : 'False'}</span>
                                </p>
                            </div>
                        </div>
                    )}

                    {Boolean(data.old_values.isOpenViaChecked) !== Boolean(data.new_values.isOpenViaChecked) && (
                        <div className={styles.row}>
                            <div className={styles.col}>
                                <p>
                                    Open Via: <span>{data.old_values.isOpenViaChecked ? 'True' : 'False'}</span>
                                </p>
                            </div>
                            <div className={styles.col}>
                                <p>
                                    Open Via: <span>{data.new_values.isOpenViaChecked ? 'True' : 'False'}</span>
                                </p>
                            </div>
                        </div>
                    )}

                    {Boolean(data.old_values.errorNotification) !== Boolean(data.new_values.errorNotification) && (
                        <div className={styles.row}>
                            <div className={styles.col}>
                                <p>
                                    Error Notification:{' '}
                                    <span>
                                        {data.old_values.errorNotification !== undefined
                                            ? data.old_values.errorNotification
                                                ? 'True'
                                                : 'False'
                                            : 'True'}
                                    </span>
                                </p>
                            </div>
                            <div className={styles.col}>
                                <p>
                                    Error Notification:{' '}
                                    <span>
                                        {data.new_values.errorNotification !== undefined
                                            ? data.new_values.errorNotification
                                                ? 'True'
                                                : 'False'
                                            : 'True'}
                                    </span>
                                </p>
                            </div>
                        </div>
                    )}

                    {hasChanged(data?.old_values.leasingAgentEmailId, data?.new_values.leasingAgentEmailId) &&
                        data.old_values.leasingAgentEmailId !== data.new_values.leasingAgentEmailId && (
                            <div className={styles.row}>
                                <div className={styles.col}>
                                    <p>
                                        Leasing Agent Email Id: <span>{data.old_values.leasingAgentEmailId}</span>
                                    </p>
                                </div>
                                <div className={styles.col}>
                                    <p>
                                        Leasing Agent Email Id: <span>{data.new_values.leasingAgentEmailId}</span>
                                    </p>
                                </div>
                            </div>
                        )}

                    {(data.old_values.leasingAgentNumber
                        ? data.old_values.leasingAgentNumber?.toString()
                        : data.old_values.leasingAgentNumber) !==
                        (data.new_values.leasingAgentNumber
                            ? data.new_values.leasingAgentNumber?.toString()
                            : data.new_values.leasingAgentNumber) && (
                            <div className={styles.row}>
                                <div className={styles.col}>
                                    <p>
                                        Leasing Agent Number:{' '}
                                        <span>
                                            {data?.old_values?.leasingAgentNumber
                                                ? `${formatPhone(`+ ${data.old_values.leasingAgentNumber?.toString()}`)}`
                                                : ''}
                                        </span>
                                    </p>
                                </div>
                                <div className={styles.col}>
                                    <p>
                                        Leasing Agent Number:{' '}
                                        <span>
                                            {data?.new_values?.leasingAgentNumber
                                                ? `${formatPhone(`+ ${data.new_values.leasingAgentNumber?.toString()}`)}`
                                                : ''}
                                        </span>
                                    </p>
                                </div>
                            </div>
                        )}

                    {data?.old_values?.radius !== data?.new_values?.radius && (
                        <div className={styles.row}>
                            <div className={styles.col}>
                                <p>
                                    Radius: <span>{data?.old_values?.radius ? data?.old_values?.radius : ''}</span>
                                </p>
                            </div>
                            <div className={styles.col}>
                                <p>
                                    Radius: <span>{data?.new_values?.radius ? data?.new_values?.radius : ''}</span>
                                </p>
                            </div>
                        </div>
                    )}

                    {data?.old_values?.googlePlacesId !== data?.new_values?.googlePlacesId && (
                        <div className={styles.row}>
                            <div className={styles.col}>
                                <p>
                                    Google Location:{' '}
                                    <span>
                                        {data?.old_values?.googlePlacesId ? <FetchPlace placeId={data?.old_values?.googlePlacesId} /> : ''}
                                    </span>
                                </p>
                            </div>
                            <div className={styles.col}>
                                <p>
                                    Google Location:{' '}
                                    <span>
                                        {data?.new_values?.googlePlacesId ? <FetchPlace placeId={data?.new_values?.googlePlacesId} /> : ''}
                                    </span>
                                </p>
                            </div>
                        </div>
                    )}

                    {hasChanged(data?.old_values.amazonKeyAccessId, data?.new_values.amazonKeyAccessId) &&
                        data?.old_values?.amazonKeyAccessId !== data?.new_values?.amazonKeyAccessId && (
                            <div className={styles.row}>
                                <div className={styles.col}>
                                    <p>
                                        Amazon Door Access Point Id: <span>{data?.old_values?.amazonKeyAccessId || ''}</span>
                                    </p>
                                </div>
                                <div className={styles.col}>
                                    <p>
                                        Amazon Door Access Point Id: <span>{data?.new_values?.amazonKeyAccessId || ''}</span>
                                    </p>
                                </div>
                            </div>
                        )}

                    {((isEmptyOrNull(data.old_values.customCardDisclaimer) !== isEmptyOrNull(data.new_values.customCardDisclaimer) &&
                        data.old_values.customCardDisclaimer !== data.new_values.customCardDisclaimer) ||
                        (!isEmptyOrNull(data.old_values.customCardDisclaimer) &&
                            !isEmptyOrNull(data.new_values.customCardDisclaimer) &&
                            data.old_values.customCardDisclaimer !== data.new_values.customCardDisclaimer)) && (
                            <div className={styles.row}>
                                <div className={styles.col}>
                                    <p>
                                        Custom Disclaimer: <span>{data.old_values.customCardDisclaimer}</span>
                                    </p>
                                </div>
                                <div className={styles.col}>
                                    <p>
                                        Custom Disclaimer: <span>{data.new_values.customCardDisclaimer}</span>
                                    </p>
                                </div>
                            </div>
                        )}

                    {data.old_values.confirmText !== data.new_values.confirmText && (
                        <div className={styles.row}>
                            <div className={styles.col}>
                                <p>
                                    Tour Confirm Message: <span>{data.old_values.confirmText}</span>
                                </p>
                            </div>
                            <div className={styles.col}>
                                <p>
                                    Tour Confirm Message: <span>{data.new_values.confirmText}</span>
                                </p>
                            </div>
                        </div>
                    )}

                    {data.old_values.beginningTourMessage !== data.new_values.beginningTourMessage && (
                        <div className={styles.row}>
                            <div className={styles.col}>
                                <p>
                                    Tour Start Message: <span>{data.old_values.beginningTourMessage}</span>
                                </p>
                            </div>
                            <div className={styles.col}>
                                <p>
                                    Tour Start Message: <span>{data.new_values.beginningTourMessage}</span>
                                </p>
                            </div>
                        </div>
                    )}

                    {data.old_values.endingTourMessage !== data.new_values.endingTourMessage && (
                        <div className={styles.row}>
                            <div className={styles.col}>
                                <p>
                                    Tour End Message: <span>{data.old_values.endingTourMessage}</span>
                                </p>
                            </div>
                            <div className={styles.col}>
                                <p>
                                    Tour End Message: <span>{data.new_values.endingTourMessage}</span>
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default LogsAccordion;
